import type { AnnounceBannerItem } from "@/features/announce/api/announceClient";
import type { AnnouncementEvent } from "@/features/announce/mediators/announcementEvents";
import { createMediator, type Mediator } from "@/shared/lib/componentHierarchy";

export type AnnouncementBannerEvent = Extract<
  AnnouncementEvent,
  { type: "announcement/banner-opened" | "announcement/banner-dismissed" }
>;

export function isAnnouncementBannerEvent(
  event: AnnouncementEvent,
): event is AnnouncementBannerEvent {
  return (
    event.type === "announcement/banner-opened" ||
    event.type === "announcement/banner-dismissed"
  );
}

export interface AnnouncementBannerState {
  readonly dismissedIds: ReadonlySet<number>;
}

export type AnnouncementBannerEffect =
  | { readonly type: "open"; readonly banner: AnnounceBannerItem }
  | { readonly type: "persist-dismissal"; readonly bannerId: number };

export function createAnnouncementBannerMediator(
  runEffect: (effect: AnnouncementBannerEffect) => void,
): Mediator<
  AnnouncementBannerState,
  AnnouncementBannerEvent,
  AnnouncementBannerEffect
> {
  return createMediator<
    AnnouncementBannerState,
    AnnouncementBannerEvent,
    AnnouncementBannerEffect
  >(
    { dismissedIds: new Set() },
    (state, event) => {
      switch (event.type) {
        case "announcement/banner-opened":
          return {
            status: "handled",
            state,
            effects: [{ type: "open", banner: event.banner }],
          };
        case "announcement/banner-dismissed": {
          if (state.dismissedIds.has(event.bannerId)) {
            return { status: "handled", state };
          }
          const dismissedIds = new Set(state.dismissedIds);
          dismissedIds.add(event.bannerId);
          return {
            status: "handled",
            state: { dismissedIds },
            effects: [{ type: "persist-dismissal", bannerId: event.bannerId }],
          };
        }
      }
    },
    { runEffect },
  );
}
